/**
 * POPSTAR settings — account + preferences for a named user: display name,
 * email, password, and UI language. Presentational + human-timescale only; it
 * reads the current account via the {@link GameServices} surface and performs
 * only DISCRETE actions on it.
 *
 * Layout mirrors the legacy POPSTAR settings screen (AVOID_LEGACY_ANALYSIS §2.13:
 * email / display-name / password change / language), restyled in the glamorous
 * glass palette the `.settings-screen` family re-maps (deep purple → magenta with
 * a gold accent). All colour/blur/radius/shadow come from the shared design
 * tokens in `src/index.css` — nothing is inlined here.
 *
 * CONTRACT NOTE: the locked {@link GameServices} interface currently exposes only
 * `resetPassword` and `logOut` as account mutators (name/email/language setters
 * are not yet on the contract — they arrive in a later network phase). So this
 * screen shows the current name/email/language read-only, wires the password
 * change through the email reset flow, lets the user pick a language locally, and
 * offers log out. When the contract gains profile setters, the inputs here become
 * editable with a save handler — the layout is already in place.
 */
import { useCallback, useEffect, useState } from 'react';
import type { GameServices } from '../services/types';
import { RequiresAccountError } from '../services/types';
import type { UserProfile } from '../services/dataModel';
import { LANGS, useI18n } from './i18n';
import type { Lang } from './i18n';

interface SettingsScreenProps {
  services: GameServices;
  /** Back to the network shell hub. */
  onBack: () => void;
  /** Called after a successful log out (parent returns the user to anon play). */
  onLoggedOut: () => void;
}

export function SettingsScreen({ services, onBack, onLoggedOut }: SettingsScreenProps) {
  // The live UI language comes from the i18n context (persisted to localStorage and,
  // best-effort, to the account via App's onChange → services.setLanguage). Changing
  // it here re-renders the whole app in the new language immediately.
  const { lang, setLang, t } = useI18n();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The editable display-name draft (seeded from the profile once it loads).
  const [nameDraft, setNameDraft] = useState('');

  // One-shot read of the current profile to seed the displayed fields.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const p = await services.getProfile();
        if (cancelled) return;
        setProfile(p);
        setNameDraft(p.name);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof RequiresAccountError
            ? 'Log in to manage your account.'
            : err instanceof Error
              ? err.message
              : 'Could not load your settings.',
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [services]);

  // The signed-in account, read from the auth surface. Email lives on
  // `currentUser().email` (NOT `.name` — that's the display name); the profile
  // read above seeds the display name, falling back to the auth user.
  const account = services.currentUser();
  const email = account.email ?? '';
  const savedName = profile?.name ?? account.name ?? '';
  const nameDirty = nameDraft.trim().length > 0 && nameDraft.trim() !== savedName;

  /** Persist the edited display name (auth profile + self-writable users doc). */
  const saveName = useCallback(async () => {
    if (busy || !nameDirty) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = nameDraft.trim();
      await services.setDisplayName(next);
      setProfile((p) => (p ? { ...p, name: next } : p));
      setNotice(t('settings.nameSaved'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your name.');
    } finally {
      setBusy(false);
    }
  }, [busy, nameDirty, nameDraft, services, t]);

  const sendPasswordReset = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      // The contract's password change is email-based: trigger a reset link to
      // the account's email. We resolve the email from the auth user.
      const target = services.currentUser().email ?? '';
      if (!target) {
        setError('No email is associated with this account.');
        return;
      }
      await services.resetPassword(target);
      setNotice('A password reset link is on its way to your email.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the reset email.');
    } finally {
      setBusy(false);
    }
  }, [busy, services]);

  /** Re-send the email verification link (sign-up sends one; this is the retry). */
  const resendVerification = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await services.resendVerificationEmail();
      setNotice(t('settings.verificationSent'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the verification email.');
    } finally {
      setBusy(false);
    }
  }, [busy, services, t]);

  const logOut = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await services.logOut();
      onLoggedOut();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not log out.');
      setBusy(false);
    }
  }, [busy, services, onLoggedOut]);

  return (
    <div className="screen network-screen settings-screen">
      <header className="network-head">
        <button className="btn btn-ghost network-back" onClick={onBack} type="button">
          ‹ {t('common.back')}
        </button>
        <h1 className="network-title">{t('settings.title')}</h1>
      </header>

      <section className="panel">
        <h2>{t('settings.account')}</h2>
        <label className="field">
          <span className="field-label">{t('settings.displayName')}</span>
          <div className="settings-name-row">
            <input
              className="field-input"
              type="text"
              value={nameDraft}
              maxLength={24}
              onChange={(e) => setNameDraft(e.target.value)}
              placeholder="Your display name"
              autoComplete="name"
            />
            {nameDirty && (
              <button
                className="btn btn-primary settings-name-save"
                type="button"
                disabled={busy}
                onClick={() => void saveName()}
              >
                {t('settings.saveName')}
              </button>
            )}
          </div>
        </label>
        <label className="field">
          <span className="field-label">{t('settings.email')}</span>
          <input
            className="field-input"
            type="email"
            value={email}
            readOnly
            aria-readonly
            placeholder="Your account email"
            autoComplete="email"
          />
        </label>
        <p className="muted-line settings-hint">{t('settings.emailLocked')}</p>
      </section>

      {/* Unverified email: the verification link is what proves inbox ownership
          (and activates the owner/admin allowlist), so surface it prominently. */}
      {account.email && !account.emailVerified && (
        <section className="panel settings-verify">
          <h2>{t('settings.verifyTitle')}</h2>
          <p className="muted-line">{t('settings.verifyBody')}</p>
          <button
            className="btn btn-primary settings-action"
            type="button"
            onClick={() => void resendVerification()}
            disabled={busy}
          >
            {t('settings.resendVerification')}
          </button>
        </section>
      )}

      <section className="panel">
        <h2>{t('settings.password')}</h2>
        <p className="muted-line">{t('settings.passwordBlurb')}</p>
        <button
          className="btn btn-ghost settings-action"
          type="button"
          onClick={() => void sendPasswordReset()}
          disabled={busy}
        >
          {t('settings.sendReset')}
        </button>
      </section>

      <section className="panel">
        <h2>{t('settings.language')}</h2>
        <label className="field">
          <span className="field-label">{t('settings.uiLanguage')}</span>
          <select
            className="field-input"
            value={lang}
            onChange={(e) => setLang(e.target.value as Lang)}
          >
            {LANGS.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
        </label>
        <p className="muted-line settings-hint">{t('settings.langHint')}</p>
      </section>

      {error && (
        <p className="auth-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="auth-notice" role="status">
          {notice}
        </p>
      )}

      <button
        className="btn btn-danger settings-logout"
        type="button"
        onClick={() => void logOut()}
        disabled={busy}
      >
        {t('settings.logout')}
      </button>

      <style>{`
        /* Scoped, settings-only refinements. Colours/blur/radius/shadow and the
           shared bits (.settings-screen .panel width, .settings-logout width,
           .settings-hint, the glamorous palette re-map) live in src/index.css;
           this block only adds the layout the global sheet does not own. */
        .settings-screen {
          gap: var(--space-4);
        }
        /* Password CTA is full-width so it reads as the panel's primary action. */
        .settings-action {
          width: 100%;
          margin-top: var(--space-3);
        }
        /* Editable name: input + save button side by side. */
        .settings-name-row {
          display: flex;
          gap: 8px;
          align-items: stretch;
        }
        .settings-name-row .field-input { flex: 1; min-width: 0; }
        .settings-name-save { flex: 0 0 auto; padding: 0 16px; }
      `}</style>
    </div>
  );
}
